import {Injectable} from "@angular/core";

import AES from "crypto-js/aes";

import Utf8 from "crypto-js/enc-utf8";

import SHA256 from "crypto-js/sha256";

@Injectable()
export class StorageService {

    /**
     * Stored data
     * @type {Object}
     */
    private data: Object;

    /**
     * Secret phrase
     * @type {string}
     */
    private phrase: string = "~!@#$%^&*()_+";

    /**
     * Prefixes of localStorage keys that must survive clean()
     * (e.g. trusted device tokens, kept per-username outside the encrypted cache blob)
     * @type {string[]}
     */
    private persistentPrefixes: string[] = ["device_token"];

    public constructor() {
        this.restore();
    }

    /**
     * Restore data from cache (local storage)
     * @returns {void}
     */
    private restore(): void {
        const encrypted: string = window.localStorage.getItem("cache");
        if (encrypted) {
            const decrypted: string = AES.decrypt(encrypted, this.phrase).toString(Utf8);
            try {
                this.data = JSON.parse(decrypted);
            } catch (e) {
            }
        } else {
            this.data = {};
        }
    }

    /**
     * Store data in cache (local storage)
     * @returns {void}
     */
    private store(): void {
        if (Object.keys(this.data).length) {
            window.localStorage.setItem("cache", AES.encrypt(JSON.stringify(this.data), this.phrase).toString());
        }
    }

    /**
     * Clean storage
     * @returns {void}
     */
    public clean(): void {
        // Snapshot persistent keys (trusted device tokens) so they survive the wipe
        const preserved: { [key: string]: string } = {};
        for (let i: number = 0; i < window.localStorage.length; i++) {
            const key: string = window.localStorage.key(i);
            if (key && this.isPersistentKey(key)) {
                preserved[key] = window.localStorage.getItem(key);
            }
        }
        this.data = {};
        window.localStorage.clear();
        for (const key of Object.keys(preserved)) {
            window.localStorage.setItem(key, preserved[key]);
        }
    }

    /**
     * Whether a localStorage key must be preserved across clean()
     * @param {string} key
     * @returns {boolean}
     */
    private isPersistentKey(key: string): boolean {
        return this.persistentPrefixes.some((prefix: string): boolean => key.indexOf(prefix) === 0);
    }

    /**
     * Get a raw persistent value (stored outside the encrypted cache blob)
     * @param {string} key
     * @returns {string}
     */
    public getPersistent(key: string): string {
        return window.localStorage.getItem(key);
    }

    /**
     * Set a raw persistent value (survives clean())
     * @param {string} key
     * @param {string} value
     * @returns {void}
     */
    public setPersistent(key: string, value: string): void {
        window.localStorage.setItem(key, value);
    }

    /**
     * Remove a raw persistent value
     * @param {string} key
     * @returns {void}
     */
    public removePersistent(key: string): void {
        window.localStorage.removeItem(key);
    }

    /**
     * localStorage key holding the trusted device token for a given user.
     * The username (an email) is hashed so it is not stored in clear text.
     * @param {string} username
     * @returns {string}
     */
    public deviceTokenKey(username: string): string {
        return "device_token:" + SHA256((username || "").trim().toLowerCase()).toString();
    }

    /**
     * Read the trusted device token stored for a given user (if any)
     * @param {string} username
     * @returns {string}
     */
    public getDeviceToken(username: string): string {
        return this.getPersistent(this.deviceTokenKey(username));
    }

    /**
     * Remove all raw persistent values whose key starts with the given prefix
     * @param {string} prefix
     * @returns {void}
     */
    public clearPersistent(prefix: string): void {
        const keys: string[] = [];
        for (let i: number = 0; i < window.localStorage.length; i++) {
            const key: string = window.localStorage.key(i);
            if (key && key.indexOf(prefix) === 0) {
                keys.push(key);
            }
        }
        keys.forEach((key: string): void => window.localStorage.removeItem(key));
    }

    /**
     * Get value from storage
     * @param {string} key
     * @param defaultValue
     * @returns {any}
     */
    public get(key: string, defaultValue: any = null): any {
        return this.data[key] !== undefined ? this.data[key] : defaultValue;
    }

    /**
     * Remove value from storage
     * @param {string} key
     * @returns {void}
     */
    public remove(key: string): void {
        if (key) {
            delete this.data[key];
        }
    }

    /**
     * Set value to storage
     * @param {string} key
     * @param value
     * @returns {void}
     */
    public set(key: string, value: any): void {
        this.data[key] = value;
        this.store();
    }

}
